import { useEffect } from 'react';
import { useSWRConfig } from 'swr';
import { retainAppSocket, getAppSocket } from '../services/socketService';

const isRealtimeKey = (key: unknown): key is string => {
  if (typeof key !== 'string') return false;
  if (key.startsWith('/system-health')) return true;
  if (key.startsWith('/notifications')) return true;
  if (key.startsWith('/emails/outbox')) return true;
  return false;
};

/** Revalidates SWR keys on Socket.IO events (system health, notifications, outbox). */
export function useRealtimeInvalidation(enabled: boolean): void {
  const { mutate } = useSWRConfig();

  useEffect(() => {
    if (!enabled) return;

    const release = retainAppSocket();
    if (!release) return;

    const socket = getAppSocket();
    if (!socket) {
      release();
      return;
    }

    const revalidate = () => {
      void mutate((key) => isRealtimeKey(key), undefined, { revalidate: true });
    };

    socket.on('sync-logs:invalidate', revalidate);
    socket.on('emails:invalidate', revalidate);
    socket.on('notifications:invalidate', revalidate);

    return () => {
      socket.off('sync-logs:invalidate', revalidate);
      socket.off('emails:invalidate', revalidate);
      socket.off('notifications:invalidate', revalidate);
      release();
    };
  }, [enabled, mutate]);
}
